import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Music,
  Calendar,
  Download,
  Clock,
  FileText,
  Sparkles,
  AlertCircle,
  TrendingUp,
  Album,
  User,
  Zap,
  Cpu,
  Package,
  Award,
  FileCode,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { SequenceDetail } from '@/types/sequence';
import { formatDistanceToNow, format } from 'date-fns';
import { formatDuration, formatHolidayName } from '@/utils/formatters';
import { VideoPlayer } from '@/components/shared/VideoPlayer';

interface SequenceMetadataProps {
  sequence: SequenceDetail;
}

const SequenceMetadata: React.FC<SequenceMetadataProps> = ({ sequence }) => {
  const song = sequence.song;
  const hasSpotifyData = !!(song?.spotifyTrackId || song?.album || song?.spotifyPopularity);
  const [showImportNotes, setShowImportNotes] = React.useState(false);

  // Helper to get controller type badge color
  const getControllerTypeBadge = (type: string) => {
    switch (type) {
      case 'AC_ONLY':
        return { label: 'AC Only', className: 'bg-blue-100 text-blue-700 border-blue-300' };
      case 'RGB':
        return { label: 'RGB (Pixel)', className: 'bg-purple-100 text-purple-700 border-purple-300' };
      case 'MIXED':
        return { label: 'Mixed (AC + RGB)', className: 'bg-green-100 text-green-700 border-green-300' };
      default:
        return { label: type, className: '' };
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Sequence Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Song Information with Album Art */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Song Information
          </h4>
          
          <div className="flex gap-4">
            {(song?.imageUrl || sequence.album_cover_url) && (
              <div className="shrink-0">
                <img 
                  src={song?.imageUrl || sequence.album_cover_url} 
                  alt={`${song?.album || song?.title || sequence.title} cover`}
                  className="w-32 h-32 rounded-md object-cover shadow-xs"
                />
              </div>
            )}
            
            <div className="flex-1 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{song?.title || sequence.title}</p>
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {(() => {
                      const artistName = song?.artist || sequence.artist || 'Unknown Artist';
                      const isValidArtist = artistName && artistName !== 'Unknown Artist';

                      if (isValidArtist) {
                        return (
                          <Link
                            to={`/sequences/search?sequences[refinementList][artist_name][0]=${encodeURIComponent(artistName)}`}
                            className="inline-block"
                          >
                            <Badge variant="outline" className="border-2 hover:bg-accent hover:border-primary transition-colors cursor-pointer">
                              {artistName}
                            </Badge>
                          </Link>
                        );
                      }
                      return <span>{artistName}</span>;
                    })()}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {(song?.spotifyTrackId || sequence.spotify_track_id) && (
                    <Badge variant="outline" className="text-xs bg-spotify-green/10 border-spotify-green/30 text-spotify-green shrink-0">
                      <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                      Spotify
                    </Badge>
                  )}
                  {song?.spotifyExplicit && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Explicit
                    </Badge>
                  )}
                </div>
              </div>
              
              {(song?.album || sequence.album) && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Album className="h-3 w-3" />
                  <Link
                    to={`/sequences/search?sequences[refinementList][album][0]=${encodeURIComponent(song?.album || sequence.album)}`}
                    className="inline-block"
                  >
                    <Badge variant="outline" className="border-2 hover:bg-accent hover:border-primary transition-colors cursor-pointer">
                      {song?.album || sequence.album}
                    </Badge>
                  </Link>
                </div>
              )}
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDuration(song?.duration || sequence.duration, song?.spotifyDurationMs || sequence.spotify_duration_ms)}
                </span>
                {song?.genre && (
                  <span className="flex items-center gap-1">
                    <Music className="h-3 w-3" />
                    <Link
                      to={`/sequences/search?sequences[refinementList][genre_canonical][0]=${encodeURIComponent(song.genre)}`}
                      className="inline-block"
                    >
                      <Badge variant="outline" className="border-2 hover:bg-accent hover:border-primary transition-colors cursor-pointer">
                        {song.genre}
                      </Badge>
                    </Link>
                  </span>
                )}
                {song?.spotifyPopularity && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {song.spotifyPopularity}% popular
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Video Showcase - Show if video exists */}
        {sequence.videoUrl && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Video Showcase
              </h4>
              <VideoPlayer
                videoUrl={sequence.videoUrl || ''}
                title={sequence.title}
                imageUrl={sequence.album_cover_url || sequence.imageUrl}
                aspectRatio="video"
              />
            </div>
          </>
        )}

        <Separator />

        {/* Technical Details */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Lighting Nerd Info
          </h4>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Software</p>
              <Link
                to={`/sequences/search?sequences[refinementList][software_type][0]=${encodeURIComponent(sequence.software)}`}
                className="inline-block mt-1"
              >
                <Badge variant="outline" className="border-2 hover:bg-accent hover:border-primary transition-colors cursor-pointer">
                  {sequence.software}
                </Badge>
              </Link>
            </div>

            <div>
              <p className="text-muted-foreground text-xs">Holiday</p>
              <Link
                to={`/sequences/search?sequences[refinementList][holiday_type][0]=${encodeURIComponent(sequence.holiday)}`}
                className="inline-block mt-1"
              >
                <Badge variant="outline" className="border-2 hover:bg-accent hover:border-primary transition-colors cursor-pointer">
                  {formatHolidayName(sequence.holiday)}
                </Badge>
              </Link>
            </div>
            
            {sequence.channelCount && (
              <div>
                <p className="text-muted-foreground text-xs">Channels</p>
                <p className="font-medium">{sequence.channelCount.toLocaleString()}</p>
              </div>
            )}
            
            <div>
              <p className="text-muted-foreground text-xs">Duration</p>
              <p className="font-medium">{formatDuration(sequence.duration, sequence.spotify_duration_ms)}</p>
            </div>
            
            <div>
              <p className="text-muted-foreground text-xs">Files Included</p>
              <p className="font-medium">{sequence.file_type || 'Sequence file'}</p>
            </div>
          </div>
        </div>

        {/* Props & Equipment - Show if sequence has props */}
        {sequence.props && sequence.props.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Package className="h-4 w-4" />
                Props & Equipment ({sequence.props.length})
              </h4>

              <div className="flex flex-wrap gap-2">
                {sequence.props.map((prop) => (
                  <Badge
                    key={prop.id}
                    variant="outline"
                    className="border-2 bg-blue-50 text-blue-700 border-blue-300"
                  >
                    {prop.name}
                    {prop.quantity && prop.quantity > 1 && (
                      <span className="ml-1.5 text-xs opacity-75">× {prop.quantity}</span>
                    )}
                  </Badge>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                These are the physical props and equipment required to run this sequence
              </p>
            </div>
          </>
        )}

        {/* Light-O-Rama Specific Metadata - Only for LOR sequences */}
        {sequence.lor_metadata && (sequence.software === 'Light-O-Rama' || sequence.software === 'LOR') && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Light-O-Rama Details
              </h4>

              <div className="space-y-3 text-sm">
                {/* Controller Type */}
                <div>
                  <p className="text-muted-foreground text-xs mb-1.5 flex items-center gap-1">
                    <Cpu className="h-3 w-3" />
                    Controller Type
                  </p>
                  <Badge
                    variant="outline"
                    className={`${getControllerTypeBadge(sequence.lor_metadata.controller_type).className} border-2`}
                  >
                    {getControllerTypeBadge(sequence.lor_metadata.controller_type).label}
                  </Badge>
                </div>

                {/* Channel Count (AC) - only if exists */}
                {sequence.lor_metadata.channel_count_ac && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Channel Count (AC)</p>
                    <p className="font-medium">{sequence.lor_metadata.channel_count_ac.toLocaleString()} channels</p>
                  </div>
                )}

                {/* Software Version */}
                {sequence.lor_metadata.software_version && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1.5">Software Version Required</p>
                    <Badge variant="outline" className="border-2 bg-amber-50 text-amber-700 border-amber-300">
                      LOR {sequence.lor_metadata.software_version}
                    </Badge>
                  </div>
                )}

                {/* License Level */}
                {sequence.lor_metadata.license_level_required && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1.5 flex items-center gap-1">
                      <Award className="h-3 w-3" />
                      License Level Required
                    </p>
                    <Badge
                      variant="outline"
                      className="border-2 bg-indigo-50 text-indigo-700 border-indigo-300"
                    >
                      {sequence.lor_metadata.license_level_required}
                    </Badge>
                  </div>
                )}

                {/* Prop Types */}
                {sequence.lor_metadata.prop_types && sequence.lor_metadata.prop_types.length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1.5 flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      Props Used ({sequence.lor_metadata.prop_types.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                      {sequence.lor_metadata.prop_types.map((prop, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="text-xs"
                        >
                          {prop}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sequence Type Tags */}
                {sequence.lor_metadata.sequence_type_tags && sequence.lor_metadata.sequence_type_tags.length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1.5 flex items-center gap-1">
                      <FileCode className="h-3 w-3" />
                      Sequence Type
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {sequence.lor_metadata.sequence_type_tags.map((tag, index) => {
                        const tagLabel = tag.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                        return (
                          <Badge
                            key={index}
                            variant="outline"
                            className="border-2 bg-teal-50 text-teal-700 border-teal-300"
                          >
                            {tagLabel}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Import Notes - Collapsible */}
                {sequence.lor_metadata.import_notes && sequence.lor_metadata.import_notes.trim() !== '' && (
                  <div>
                    <button
                      onClick={() => setShowImportNotes(!showImportNotes)}
                      className="text-muted-foreground text-xs mb-1.5 flex items-center gap-1 hover:text-foreground transition-colors w-full text-left"
                    >
                      {showImportNotes ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      Import Notes
                    </button>
                    {showImportNotes && (
                      <div className="bg-muted/50 p-3 rounded-md text-xs whitespace-pre-wrap">
                        {sequence.lor_metadata.import_notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <Separator />

        {/* Statistics */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Statistics
          </h4>
          
          <div className="space-y-2 text-sm">
            {sequence.downloadCount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Download className="h-3 w-3" />
                  Downloads
                </span>
                <span className="font-medium">{sequence.downloadCount.toLocaleString()}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Added
              </span>
              <span className="font-medium">
                {format(new Date(sequence.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
            
            {sequence.updatedAt && sequence.updatedAt !== sequence.createdAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last Updated
                </span>
                <span className="font-medium">
                  {formatDistanceToNow(new Date(sequence.updatedAt), { addSuffix: true })}
                </span>
              </div>
            )}
            
            {sequence.featured && (
              <div className="pt-2">
                <Badge variant="default" className="w-full justify-center">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Featured Sequence
                </Badge>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SequenceMetadata;